import type { IFileSystemContext, FileSystemContextConfig, FileStat } from './types'

/**
 * 轻量浏览器文件系统上下文实现
 * - 以 localStorage 持久化（可选），内存为主
 * - 路径使用类似 /dir/sub/file.txt 的形式
 */
class BrowserFileSystemContext implements IFileSystemContext {
  public readonly id: string
  private readonly inheritGlobal: boolean
  private readonly baseDir: string
  private readonly storage: Storage | null
  private readonly parent?: IFileSystemContext

  // 文件数据表：标准化路径 -> { data: string, binary: boolean, size, mtime }
  private files = new Map<string, { data: string; binary: boolean; size: number; mtime: number }>()
  // 目录集合（显式 mkdir 创建）
  private dirs = new Set<string>()
  private destroyed = false

  constructor(id: string, config: FileSystemContextConfig & { storage?: Storage | null; parent?: IFileSystemContext } = {}) {
    this.id = id
    this.inheritGlobal = !!config.inheritGlobal
    this.baseDir = this._normalizeDir(config.baseDir || '/')
    this.storage = config.storage ?? (typeof localStorage !== 'undefined' ? localStorage : null)
    this.parent = config.parent

    this._loadFromStorage()
  }

  async writeFile(path: string, data: string | ArrayBuffer): Promise<void> {
    this._check()
    const npath = this._normalizePath(path)

    // 确保父目录存在（逻辑目录）
    await this._ensureParentDirs(npath)

    let payload: string
    let binary = false
    if (typeof data === 'string') {
      payload = data
    } else {
      // ArrayBuffer -> base64
      payload = this._arrayBufferToBase64(data)
      binary = true
    }

    const now = Date.now()
    const size = binary ? payload.length : new Blob([payload]).size
    this.files.set(npath, { data: payload, binary, size: Number(size), mtime: now })

    this._persist()
  }

  async readFile(path: string, asBinary = false): Promise<string | ArrayBuffer> {
    this._check()
    const npath = this._normalizePath(path)
    const rec = this.files.get(npath)

    if (!rec) {
      if (this.inheritGlobal && this.parent) {
        return this.parent.readFile(this._withBase(npath), asBinary)
      }
      throw new Error(`File not found: ${path}`)
    }

    if (rec.binary) {
      const buf = this._base64ToArrayBuffer(rec.data)
      return asBinary ? buf : new TextDecoder().decode(buf)
    } else {
      return asBinary ? new TextEncoder().encode(rec.data).buffer : rec.data
    }
  }

  async deleteFile(path: string): Promise<boolean> {
    this._check()
    const npath = this._normalizePath(path)
    const existed = this.files.delete(npath)
    this._persist()
    return existed
  }

  async exists(path: string): Promise<boolean> {
    this._check()
    const npath = this._normalizePath(path)
    if (this.files.has(npath) || this.dirs.has(this._normalizeDir(npath))) return true
    if (this.inheritGlobal && this.parent) return this.parent.exists(this._withBase(npath))
    return false
  }

  async list(dir: string): Promise<FileStat[]> {
    this._check()
    const ndir = this._normalizeDir(dir)

    const children = new Map<string, FileStat>()

    // 列出当前上下文下的直接子项
    for (const [p, rec] of this.files) {
      if (!p.startsWith(ndir)) continue
      const rest = p.slice(ndir.length)
      if (rest.length === 0) continue
      const seg = rest.split('/')[0]
      const childPath = ndir + seg
      const isDir = rest.includes('/')
      if (isDir) {
        children.set(childPath + '/', {
          path: childPath + '/',
          isDir: true,
          size: 0,
          mtime: rec.mtime,
        })
      } else {
        children.set(childPath, {
          path: childPath,
          isDir: false,
          size: rec.size,
          mtime: rec.mtime,
        })
      }
    }

    // 显式创建的目录
    for (const d of this.dirs) {
      if (!d.startsWith(ndir)) continue
      const rest = d.slice(ndir.length)
      if (rest.length === 0) continue
      const seg = rest.split('/')[0]
      const childPath = ndir + seg + '/'
      children.set(childPath, {
        path: childPath,
        isDir: true,
        size: 0,
        mtime: 0,
      })
    }

    // 继承父级（只读透传）
    if (this.inheritGlobal && this.parent) {
      const parentList = await this.parent.list(this._withBase(ndir))
      for (const st of parentList) {
        const mappedPath = this._stripBase(st.path)
        const key = st.isDir ? this._normalizeDir(mappedPath) : this._normalizePath(mappedPath)
        if (!children.has(key)) {
          children.set(key, { ...st, path: key })
        }
      }
    }

    return Array.from(children.values()).sort((a, b) => a.path.localeCompare(b.path))
  }

  async mkdir(dir: string): Promise<void> {
    this._check()
    const ndir = this._normalizeDir(dir)
    this.dirs.add(ndir)
    this._persist()
  }

  getStats(): { id: string; files: number; directories: number; inheritGlobal: boolean } {
    return {
      id: this.id,
      files: this.files.size,
      directories: this.dirs.size,
      inheritGlobal: this.inheritGlobal,
    }
  }

  destroy(): void {
    this.destroyed = true
  }

  // ========== 内部工具 ==========

  private _check() {
    if (this.destroyed) throw new Error('FileSystemContext destroyed')
  }

  private _normalizePath(path: string): string {
    const p = ('/' + path).replace(/\\/g, '/').replace(/\/+/g, '/')
    const norm = p.endsWith('/') ? p.slice(0, -1) : p
    return this.baseDir === '/' ? norm : this.baseDir + (norm.startsWith('/') ? norm : '/' + norm)
  }

  private _normalizeDir(path: string): string {
    const p = ('/' + path).replace(/\\/g, '/').replace(/\/+/g, '/')
    const withSlash = p.endsWith('/') ? p : p + '/'
    return this.baseDir === '/' ? withSlash : this.baseDir + (withSlash.startsWith('/') ? withSlash : '/' + withSlash)
  }

  private _withBase(path: string): string {
    // 映射到父级：当前上下文的逻辑路径拼入父级
    return path
  }

  private _stripBase(path: string): string {
    if (this.baseDir === '/') return path
    return path.startsWith(this.baseDir) ? path.slice(this.baseDir.length) || '/' : path
  }

  private _persist() {
    if (!this.storage) return
    const key = `mflow:fs:${this.id}`
    const data = {
      files: Array.from(this.files.entries()),
      dirs: Array.from(this.dirs.values()),
      baseDir: this.baseDir,
      inheritGlobal: this.inheritGlobal,
    }
    try {
      this.storage.setItem(key, JSON.stringify(data))
    } catch {}
  }

  private _loadFromStorage() {
    if (!this.storage) return
    const key = `mflow:fs:${this.id}`
    const raw = this.storage.getItem(key)
    if (!raw) return
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed.files)) {
        this.files = new Map(parsed.files)
      }
      if (Array.isArray(parsed.dirs)) {
        this.dirs = new Set(parsed.dirs)
      }
    } catch {}
  }

  private async _ensureParentDirs(npath: string) {
    const parts = npath.split('/')
    parts.pop() // remove file
    let cur = '/'
    for (const seg of parts.filter(Boolean)) {
      cur = (cur.endsWith('/') ? cur : cur + '/') + seg + '/'
      this.dirs.add(cur)
    }
  }

  private _arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = ''
    const bytes = new Uint8Array(buffer)
    const len = bytes.byteLength
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i])
    }
    return btoa(binary)
  }

  private _base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary_string = atob(base64)
    const len = binary_string.length
    const bytes = new Uint8Array(len)
    for (let i = 0; i < len; i++) {
      bytes[i] = binary_string.charCodeAt(i)
    }
    return bytes.buffer
  }
}

let defaultFs: IFileSystemContext | null = null

/**
 * 获取默认文件系统（单例）
 */
export function getDefaultFileSystem(): IFileSystemContext {
  if (!defaultFs) {
    defaultFs = new BrowserFileSystemContext('default', { inheritGlobal: false })
  }
  return defaultFs
}

export { BrowserFileSystemContext }